import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Stack,
    Text,
    Textarea,
    useColorModeValue,
    Alert,
    AlertIcon,
    Link, FormErrorMessage,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Card from "../../../components/Card/Card";
import CardHeader from "../../../components/Card/CardHeader";
import CardBody from "../../../components/Card/CardBody";
import Loading from "../../../components/Loading/Loading";
import { useNavigate, useParams } from "react-router-dom";
import { PROCESS_STATUS, processesService } from "../../../services/processesService";
import SearchSelect from "../../../components/SearchSelect/SearchSelect";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { ROUTES } from "../../../constants/routes-path";
import DateTime from "react-datetime";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

const ProcessConfirmReceipt = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const { id } = useParams();
    const [data, setData] = useState({
        data_limite_julgamento: "",
        observacoes: "",
        id_usuario_responsavel_atual: "",
    });
    const [formErrors, setFormErrors] = useState({});
    const [processData, setProcessData] = useState({});
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const textColor = useColorModeValue("gray.700", "white");

    const isEditable = () => processData?.status === PROCESS_STATUS.CREATED;

    useEffect(() => {
        (async () => {
            await fetchProcess();
            await fetchUsers();
        })();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await processesService.lookupUsers();
            const userList = response || [];

            setUsers(userList);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
        finally {
            setLoading(false);
        }
    };

    const fetchProcess = async () => {
        setLoading(true);
        try {
            const process = await processesService.find(id);
            setProcessData(process);
        } catch (error) {
            showToast({ title: t("process.messages.errors.find"), status: "error" });
            redirectToList();
        } finally {
            setLoading(false);
        }
    };

    const validateField = (name, value) => {
        if ((name === "id_usuario_responsavel_atual" && processData?.process_responsible_needed) ||
            (name === "data_limite_julgamento" && processData?.process_judgement_meetings_needed)) {
            if (!value || value === "") {
                return t("system.messages.errors.required-field");
            }
        }
        return undefined;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        setFormErrors(prev => ({ ...prev, [name]: error }));
        setData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (date) => {
        const m = moment(date);
        const formatted = m && m.isValid()
            ? m.format(DATE_FORMAT_ISO)
            : "";

        const error = validateField("data_limite_julgamento", m);

        setFormErrors(prev => ({
            ...prev,
            data_limite_julgamento: error
        }));

        setData(prev => ({
            ...prev,
            data_limite_julgamento: formatted
        }));
    };

    const handleSelectChange = (e) => {
        const { value } = e.target;
        const error = validateField("id_usuario_responsavel_atual", value);
        setFormErrors(prev => ({ ...prev, id_usuario_responsavel_atual: error }));
        setData(prev => ({ ...prev, id_usuario_responsavel_atual: value }));
    };

    const isSaveDisabled = () => {
        const requiredFields = {
            ...(processData?.process_responsible_needed && {
                id_usuario_responsavel_atual: data.id_usuario_responsavel_atual
            }),
            ...(processData?.process_judgement_meetings_needed && {
                data_limite_julgamento: data.data_limite_julgamento
            })
        };

        const hasEmptyRequired = Object.values(requiredFields).some(val => !val || val === "");
        const hasErrors = Object.values(formErrors).some(error => error);

        return hasEmptyRequired || hasErrors || loading;
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const formattedData = {...data, usuario_responsavel_atual: {id: data.id_usuario_responsavel_atual}};
            await processesService.confirmReceipt(processData?.id, formattedData);
            showToast({ title: t("system.notifications.success"), status: "success" });
            navigate(-1);
        } catch (err) {
            if (err === "Data de julgamento deve ser maior que a data atual") {
                const error = validateField("data_limite_julgamento", data.data_limite_julgamento);
                setFormErrors(prev => ({
                    ...prev,
                    data_limite_julgamento: error
                }));
            } else {
                setFormErrors({ general: err });
            }
            showToast({ title: t(err), status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes);
    };

    return processData ? (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card>
                <CardHeader>
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        {t("process.confirm-receipt.title")}
                    </Text>
                </CardHeader>
                <CardBody>
                    { loading  &&  (
                        <Box
                            position="fixed"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            bg="rgba(255, 255, 255, 0.6)"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            zIndex="overlay"
                        >
                            <Loading maxH={{ sm: "100px", md: "350px" }} />
                        </Box>
                    )}
                    {isEditable()}

                    {processData?.process_responsible_needed && (
                        <FormControl isRequired isInvalid={!!formErrors.id_usuario_responsavel_atual}>
                            <FormLabel>{t("process.confirm-receipt.responsible")}</FormLabel>
                            <SearchSelect
                                items={users}
                                name="id_usuario_responsavel_atual"
                                valueField="id"
                                displayField="nome_pessoa"
                                value={data.id_usuario_responsavel_atual}
                                onChange={handleSelectChange}
                                placeholder={t("process.confirm-receipt.responsible-placeholder")}
                            />
                            <FormErrorMessage>{formErrors?.id_usuario_responsavel_atual}</FormErrorMessage>
                        </FormControl>
                    )}

                    {processData?.process_judgement_meetings_needed && (
                        <FormControl mt="4" isRequired isInvalid={!!formErrors.data_limite_julgamento}>
                            <FormLabel>{t("process.confirm-receipt.trial-limit")}</FormLabel>
                            <DateTime
                                dateFormat={DATE_FORMAT}
                                timeFormat={false}
                                locale={LOCALE_PT_BR}
                                value={
                                    data.data_limite_julgamento
                                        ? moment(data.data_limite_julgamento, DATE_FORMAT_ISO)
                                        : ""
                                }
                                renderInput={(props) => (
                                    <Input
                                        {...props}
                                        fontSize="sm"
                                        variant="main"
                                        placeholder={t("process.confirm-receipt.trial-limit-placeholder")}
                                    />
                                )}
                                onChange={handleDateChange}
                            />
                            <FormErrorMessage>{formErrors?.data_limite_julgamento}</FormErrorMessage>
                        </FormControl>
                    )}

                    <FormControl mt="4">
                        <FormLabel>{t("process.confirm-receipt.observations")}</FormLabel>
                        <Textarea
                            name="observacoes"
                            value={data.observacoes}
                            onChange={handleChange}
                            className="uppercase-field"
                        />
                    </FormControl>

                    <Stack w="100%" direction="row" justify="space-between" mt="6">
                        <Button
                            w="125px"
                            variant="no-effects"
                            bg="gray.100"
                            color="gray.700"
                            fontSize="sm"
                            fontWeight="bold"
                            onClick={redirectToList}
                        >
                            {t("system.actions.cancel")}
                        </Button>
                        <Button
                            w="125px"
                            variant="dark"
                            fontSize="sm"
                            isLoading={loading}
                            onClick={handleSave}
                            disabled={isSaveDisabled()}
                        >
                            {t("system.actions.save")}
                        </Button>
                    </Stack>
                </CardBody>
            </Card>
        </Flex>
    ) : (
        <Loading maxH={{ sm: "100px", md: "350px" }} />
    );
};

export default ProcessConfirmReceipt;
